import { useCallback, useEffect, useRef, useState } from 'react'
import { useNotificationStore } from '@/stores/notification-store'
import type { WindowConfig } from './types'
import type { GridCell, LayoutMode, ShareScope, TabGroup } from '@/types/schemas/workspace'

/**
 * Handles pending layout and panel imports from sessionStorage.
 *
 * Both dashboard and history pages use the same import flow:
 * 1. Layout export writes JSON to sessionStorage + fires a custom event
 * 2. This hook picks it up, parses it, and calls createLayout / switchLayout
 *
 * This eliminates the duplicated handlePendingLayoutImport / handlePendingPanelSwitch
 * logic that was copy-pasted across page components.
 */
export function usePendingImports({
  scope,
  loaded,
  bootstrapPromise,
  activeLayoutId,
  createLayout,
  switchLayout,
}: {
  scope: ShareScope
  loaded: boolean
  bootstrapPromise: Promise<unknown> | null
  activeLayoutId: string | null
  createLayout: (
    name: string,
    state: {
      mosaicTree: unknown
      windowConfigs: Record<string, WindowConfig>
      panels: Array<Record<string, unknown>>
      globalSettings?: Record<string, unknown>
      tabGroups?: Record<string, TabGroup>
      layoutMode?: LayoutMode
      gridLayout?: GridCell[]
    },
  ) => Promise<string | null>
  switchLayout: (layoutId: string) => void
}) {
  const [resolvedBootstrap, setResolvedBootstrap] = useState<Promise<unknown> | null>(null)
  const layoutImportInFlight = useRef(false)
  const panelImportInFlight = useRef(false)
  const panelImportRetryRequested = useRef(false)
  const [panelImportRetryNonce, setPanelImportRetryNonce] = useState(0)
  const bootstrapReady = bootstrapPromise === null || resolvedBootstrap === bootstrapPromise

  useEffect(() => {
    if (!bootstrapPromise) return
    let active = true
    bootstrapPromise.then(
      () => {
        if (active) setResolvedBootstrap(bootstrapPromise)
      },
      () => {},
    )
    return () => {
      active = false
    }
  }, [bootstrapPromise])

  // ─── Layout imports ───

  const handlePendingLayoutImport = useCallback(async () => {
    if (!loaded || !bootstrapReady || layoutImportInFlight.current) return
    const raw = sessionStorage.getItem('edge-pending-import')
    if (!raw) return
    let imported: {
      scope?: ShareScope
      name: string
      mosaicTree: unknown
      windowConfigs: Record<string, WindowConfig>
      panels: Record<string, Record<string, unknown>>
      globalSettings?: Record<string, unknown>
      tabGroups?: Record<string, TabGroup>
      layoutMode?: LayoutMode
      gridLayout?: GridCell[]
    }
    try {
      imported = JSON.parse(raw) as typeof imported
      if ((imported.scope ?? 'dashboard') !== scope) return
    } catch {
      sessionStorage.removeItem('edge-pending-import')
      useNotificationStore.getState().showError('Failed to import layout')
      return
    }

    layoutImportInFlight.current = true
    const panelsArray = Object.entries(imported.panels).map(([panelId, state]) => ({
      panelId,
      ...state,
    }))
    try {
      const createdId = await createLayout(imported.name, {
        mosaicTree: imported.mosaicTree,
        windowConfigs: imported.windowConfigs,
        panels: panelsArray,
        globalSettings: imported.globalSettings ?? {},
        ...(imported.tabGroups ? { tabGroups: imported.tabGroups } : {}),
        // Grid (scroll) layouts: structure lives in gridLayout, tree is null.
        ...(imported.layoutMode === 'grid'
          ? { layoutMode: 'grid' as const, gridLayout: imported.gridLayout ?? [] }
          : {}),
      })
      if (!createdId) throw new Error('Layout creation was rejected')
      switchLayout(createdId)
      if (sessionStorage.getItem('edge-pending-import') === raw) {
        sessionStorage.removeItem('edge-pending-import')
      }
      useNotificationStore.getState().showSuccess(`Layout "${imported.name}" imported successfully`)
    } catch {
      useNotificationStore.getState().showError('Failed to import layout')
    } finally {
      layoutImportInFlight.current = false
    }
  }, [scope, loaded, bootstrapReady, createLayout, switchLayout])

  useEffect(() => {
    handlePendingLayoutImport()
  }, [handlePendingLayoutImport])

  useEffect(() => {
    const handler = () => handlePendingLayoutImport()
    window.addEventListener('edge:pending-import', handler)
    return () => window.removeEventListener('edge:pending-import', handler)
  }, [handlePendingLayoutImport])

  // ─── Panel imports (switch to target layout) ───

  const handlePendingPanelSwitch = useCallback(async () => {
    if (!loaded || !bootstrapReady) return
    if (panelImportInFlight.current) {
      panelImportRetryRequested.current = true
      return
    }
    const raw = sessionStorage.getItem('edge-pending-panel-import')
    if (!raw) return
    try {
      const pending = JSON.parse(raw) as {
        targetLayoutId: string
        targetScope?: ShareScope
      }
      const { targetLayoutId, targetScope } = pending
      if ((targetScope ?? 'dashboard') !== scope) return
      if (!targetLayoutId) {
        panelImportInFlight.current = true
        try {
          const createdId = await createLayout('Imported panels', {
            mosaicTree: null,
            windowConfigs: {},
            panels: [],
            globalSettings: {},
          })
          if (!createdId) throw new Error('Layout creation was rejected')
          if (sessionStorage.getItem('edge-pending-panel-import') !== raw) return
          sessionStorage.setItem(
            'edge-pending-panel-import',
            JSON.stringify({ ...pending, targetLayoutId: createdId }),
          )
          switchLayout(createdId)
        } catch {
          useNotificationStore.getState().showError('Failed to prepare panel import')
        } finally {
          panelImportInFlight.current = false
          if (panelImportRetryRequested.current) {
            panelImportRetryRequested.current = false
            setPanelImportRetryNonce((nonce) => nonce + 1)
          }
        }
        return
      }
      if (targetLayoutId && targetLayoutId !== activeLayoutId) {
        switchLayout(targetLayoutId)
      }
    } catch {
      /* workspace-mosaic will handle the rest */
    }
  }, [scope, loaded, bootstrapReady, activeLayoutId, createLayout, switchLayout])

  useEffect(() => {
    void handlePendingPanelSwitch()
  }, [handlePendingPanelSwitch, panelImportRetryNonce])

  useEffect(() => {
    const handler = () => void handlePendingPanelSwitch()
    window.addEventListener('edge:pending-panel-import', handler)
    return () => window.removeEventListener('edge:pending-panel-import', handler)
  }, [handlePendingPanelSwitch])
}
